import { get, post, patch, del, put } from './axios.config';

/**
 * Serviço de gerenciamento de tenants (Admin)
 */
const tenantsService = {
  /**
   * Lista todos os tenants
   * Endpoint: GET /tenants
   * @returns {Promise<Array>}
   */
  getAll: async () => {
    try {
      const response = await get('/tenants');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao buscar tenants' };
    }
  },

  /**
   * Busca tenant por ID
   * Endpoint: GET /tenants/:id
   * @param {string} id - ID do tenant
   * @returns {Promise<Object>}
   */
  getById: async (id) => {
    try {
      const response = await get(`/tenants/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao buscar tenant' };
    }
  },

  /**
   * Cria ou atualiza tenant (upsert)
   * Endpoint: POST /tenants
   * @param {Object} tenantData - Dados do tenant
   * @returns {Promise<Object>}
   */
  createOrUpdate: async (tenantData) => {
    try {
      const response = await post('/tenants', tenantData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao criar/atualizar tenant' };
    }
  },

  /**
   * Atualiza tenant parcialmente
   * Endpoint: PATCH /tenants/:id
   * @param {string} id - ID do tenant
   * @param {Object} tenantData - Dados a atualizar
   * @returns {Promise<Object>}
   */
  update: async (id, tenantData) => {
    try {
      const response = await patch(`/tenants/${id}`, tenantData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao atualizar tenant' };
    }
  },

  /**
   * Deleta tenant
   * Endpoint: DELETE /tenants/:id
   * @param {string} id - ID do tenant
   * @returns {Promise<Object>}
   */
  delete: async (id) => {
    try {
      const response = await del(`/tenants/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao deletar tenant' };
    }
  },

  /**
   * Pausa bot do tenant
   * Endpoint: POST /tenants/:id/pause
   * @param {string} id - ID do tenant
   * @returns {Promise<Object>}
   */
  pauseBot: async (id) => {
    try {
      const response = await post(`/tenants/${id}/pause`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao pausar bot' };
    }
  },

  /**
   * Retoma bot do tenant
   * Endpoint: POST /tenants/:id/resume
   * @param {string} id - ID do tenant
   * @returns {Promise<Object>}
   */
  resumeBot: async (id) => {
    try {
      const response = await post(`/tenants/${id}/resume`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao retomar bot' };
    }
  },

  /**
   * Gera token JWT para tenant
   * Endpoint: POST /tenants/:id/token
   * @param {string} id - ID do tenant
   * @returns {Promise<Object>}
   */
  generateToken: async (id) => {
    try {
      const response = await post(`/tenants/${id}/token`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao gerar token' };
    }
  },

  /**
   * Upload de fluxo YAML
   * Endpoint: PUT /tenants/:id/flows/:flowId
   * @param {string} tenantId - ID do tenant
   * @param {string} flowId - ID do fluxo
   * @param {string} yamlContent - Conteúdo do arquivo YAML
   * @returns {Promise<Object>}
   */
  uploadFlow: async (tenantId, flowId, yamlContent) => {
    try {
      const response = await put(`/tenants/${tenantId}/flows/${flowId}`, yamlContent, {
        headers: {
          'Content-Type': 'text/plain'
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao fazer upload do fluxo' };
    }
  },

  // ============================================
  // EVOLUTION API - Gerenciamento de Instâncias
  // ============================================

  /**
   * Criar instância Evolution
   * Endpoint: POST /evolution/instances
   * @param {Object} instanceData - Dados da instância
   * @returns {Promise<Object>}
   */
  createEvolutionInstance: async (instanceData) => {
    try {
      const response = await post('/evolution/instances', instanceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao criar instância Evolution' };
    }
  },

  /**
   * Listar instâncias Evolution
   * Endpoint: GET /evolution/instances
   * @returns {Promise<Object>}
   */
  listEvolutionInstances: async () => {
    try {
      const response = await get('/evolution/instances');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao listar instâncias Evolution' };
    }
  },

  /**
   * Obter QR Code de instância
   * Endpoint: GET /evolution/instances/:instanceName/qrcode
   * @param {string} instanceName - Nome da instância
   * @returns {Promise<Object>}
   */
  getEvolutionQRCode: async (instanceName) => {
    try {
      const response = await get(`/evolution/instances/${instanceName}/qrcode`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao obter QR Code' };
    }
  },

  /**
   * Verificar status de instância
   * Endpoint: GET /evolution/instances/:instanceName/status
   * @param {string} instanceName - Nome da instância
   * @returns {Promise<Object>}
   */
  getEvolutionInstanceStatus: async (instanceName) => {
    try {
      const response = await get(`/evolution/instances/${instanceName}/status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao obter status da instância' };
    }
  },

  /**
   * Configurar webhook de instância
   * Endpoint: POST /evolution/instances/:instanceName/webhook
   * @param {string} instanceName - Nome da instância
   * @param {Object} webhookConfig - Configuração do webhook
   * @returns {Promise<Object>}
   */
  configureEvolutionWebhook: async (instanceName, webhookConfig) => {
    try {
      const response = await post(`/evolution/instances/${instanceName}/webhook`, webhookConfig);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao configurar webhook' };
    }
  },

  /**
   * Deletar instância Evolution
   * Endpoint: DELETE /evolution/instances/:instanceName
   * @param {string} instanceName - Nome da instância
   * @returns {Promise<Object>}
   */
  deleteEvolutionInstance: async (instanceName) => {
    try {
      const response = await del(`/evolution/instances/${instanceName}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao deletar instância' };
    }
  },
};

export default tenantsService;